// SlicePlugin definition for the image slice (PRD-F4 §TR1.7, §T6.2).
//
// Uses the discriminated-union CapabilityDescriptor form (PRD-X §TR3).
// Declares exactly 4 capabilities — edit-only `resize`, view-only
// `anchor-target`, view-only `stack-presence`, edit-only
// `updatable-properties`. Bewusst OHNE 'text-content', 'rotation',
// 'background-color', 'border-style', 'font-size', 'font-style',
// 'rich-edit', 'plain-edit', 'smiley-bitmap' (FC6).
//
// `isAnchorTarget` returns true unconditionally per FC8.
//
// `createDefaults` delegates to `createImageDefaults` with an empty `dataUrl`
// for plugin-API consistency. The real placement pipeline runs through the
// `ImagePlacementController`, which calls `createImageDefaults` directly
// with the dataUrl from the bus action (Variante C, PRD-F4 §TR1.6).

import type { SlicePlugin } from '../../core/object-registry.js';
import {
  type CapabilityDescriptor,
  RESIZE_EDIT,
  ANCHOR_TARGET_VIEW,
  STACK_PRESENCE_VIEW,
  UPDATABLE_PROPERTIES_EDIT,
} from '../../core/capabilities/capability.interface.js';
import type { ImageObject } from './image.interface.js';
import { createImageDefaults, fallbackImageSize } from './image-defaults.factory.js';
import { createNode, updateNode } from './image.renderer.js';

const IMAGE_CAPABILITIES: readonly CapabilityDescriptor[] = [
  { capability: 'resize', edit: RESIZE_EDIT },
  { capability: 'anchor-target', view: ANCHOR_TARGET_VIEW },
  { capability: 'stack-presence', view: STACK_PRESENCE_VIEW },
  { capability: 'updatable-properties', edit: UPDATABLE_PROPERTIES_EDIT },
];

export const imageSlice: SlicePlugin<ImageObject> = {
  type: 'image',
  role: 'primary',
  capabilities: IMAGE_CAPABILITIES,
  display: {
    iconUrl: 'image',
    label: 'Bild',
  },
  createDefaults: (scale, point, sizeOpt) =>
    createImageDefaults(
      point,
      // Plugin-created images start with no real source yet; the empty
      // data-URL is replaced once the caller supplies one.
      { dataUrl: '' },
      (sizeOpt as { width: number; height: number } | null) ?? fallbackImageSize(scale),
    ),
  createNode,
  updateNode,
  isAnchorTarget: () => true,
};
